import { isExpTenant } from '@tenants';
import AspectRatio from 'components/aspect-ratio';
import ConditionalWrapper from 'components/conditional-wrapper';
import Link from 'components/link';
import ListingCardNewOverlay from 'components/listing-card-new-overlay';
import FavouriteToggle from 'components/listing-card/favourite-toggle';
import ListingBedBathSqftLabels, { ListingBedBathSqftLabelsData } from 'components/listing-card/listing-bed-bath-sqft-labels';
import ListingImage from 'components/listing-image';
import ListingPrice, { ListingPriceData } from 'components/listing-price';
import ListingStatus from 'components/listing-status';
import ListingTimestamp, { ListingTimestampData } from 'components/listing-timestamp';
import SchemaOrg from 'components/schema-org';
import { locationsToNotUseListingModals } from 'constants/no-listing-modal-locations';
import { listingCardIds } from 'constants/test-constants';
import { useThemeContext, useUserContext } from 'contexts';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useIsMobile } from 'hooks/use-size-class';
import useWindowWidth from 'hooks/use-window-width';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { buildClassName } from 'utils/build-class-name';
import generateFullAddress from 'utils/get-full-address';
import getImageSrc from 'utils/get-image';
import { trackEvent } from 'utils/google-tag-manager';
import getListingImageAlt from 'utils/image-alt-description';
import { isListingVisibleToUser } from 'utils/compliance';
import { ProvinceOrStateCode, isUsStateCode } from 'utils/province_or_state';
import ListingCardNewImageOverlay from './components/compliance-image-overlay';
import ProviderLabels from './provider-labels';
import ProviderLogo from './provider-logo';
import styles from './style.module.scss';
import { StandardStatus } from '@zoocasa/go-search';

import type { OnListingCardClickProps } from 'utils/listing-card';

export interface ListingCardData extends ListingPriceData, ListingTimestampData, ListingBedBathSqftLabelsData {
  id: number;
  imageUrl: string | null;
  imageDesc: string | null;
  isImageReady: boolean;
  addressPathWithFallback: string;
  isCrea: boolean;
  isItsoVow: boolean;
  isPillar9Vow: boolean;
  isRebgvVow: boolean;
  city: string | null;
  province: string | null;
  isPrivate: boolean;
  isSold: boolean;
  position: {
    latitude: number;
    longitude: number;
  } | null;
  getStreet(isAuthenticated: boolean): string | null;
  providerId: number | null;
  isSignInAndTermsOfUseRequired: boolean;
  mlsNum: string | null;
  brokerage: string | null;
  displayAddress?: boolean;
  standardStatus?: StandardStatus;
  isRental: boolean;
}

export type ListingCardProps = {
  listing: ListingCardData;
  listingToCompare?: ListingBedBathSqftLabelsData;
  isWidget?: boolean;
  isImageVisible?: boolean;
  isCrawler?: boolean;
  imageDescFallback?(listing: unknown): string;
  pvrSentDate?: string;
  gtmOnClickEvent?: string;
  isOnMapSidePanel?: boolean;
  onListingCardClick: (props: OnListingCardClickProps) => void;
  isOnHomepage?: boolean;
  favouriteEventTracker?: () => void;
};

/** @deprecated Use `components/listing-card/listing-card-v2` instead */
export default function ListingCard({ listing, listingToCompare, isWidget = false, isImageVisible, isCrawler, imageDescFallback = getListingImageAlt, pvrSentDate, gtmOnClickEvent, isOnMapSidePanel = false, onListingCardClick, favouriteEventTracker }: ListingCardProps) {
  const { tenantId } = useThemeContext(); //FIXME: This is a hack to get the tenantId, we should make this component extendable without needing to use the theme context
  const useExpStyle = isExpTenant(tenantId);
  const { id: listingId, imageUrl, imageDesc = '', isImageReady, addressPathWithFallback, isCrea, isPillar9Vow,
    city, province, isPrivate: listingIsPrivate, position, getStreet, providerId, isSignInAndTermsOfUseRequired, displayAddress = true } = listing;
  const { isAuthenticated, acceptedBrowserBasedTerms, acceptedTerms } = useUserContext();
  const { formatDateWithTemplate } = useI18nFormatting();
  const [showsListing, setShowsListing] = useState<boolean>(false);
  const windowWidth = useWindowWidth();
  const listingCardElement = useRef<HTMLDivElement>(null);
  const [isSmallCard, setIsSmallCard] = useState(true);
  const isPrivate = !isAuthenticated && listingIsPrivate;
  const imageAlt = imageDesc && imageDesc.length > 0 ? imageDesc : imageDescFallback(listing);
  const street = getStreet(isAuthenticated);
  const shouldScramble = isPillar9Vow && !showsListing;
  const isNoModalLocation = province !== null && locationsToNotUseListingModals.includes(province);
  const isMobile = useIsMobile();
  const noModal = isNoModalLocation || isMobile;

  const isHidden = isPrivate || (isSignInAndTermsOfUseRequired && !showsListing && !isAuthenticated);
  const isTermsRequired = !showsListing && (isCrea || isSignInAndTermsOfUseRequired);

  useEffect(() => {
    if (providerId) {
      setShowsListing(isListingVisibleToUser(isPrivate, providerId, acceptedBrowserBasedTerms, acceptedTerms ));
    }
  }, [isPrivate, providerId, acceptedBrowserBasedTerms, acceptedTerms]);

  const openListingModal = (event: MouseEvent) => {
    if (onListingCardClick) {
      onListingCardClick({ event, isNoModalLocation, addressPathWithFallback, listingId, showsListing, isPrivate, isSignInAndTermsOfUseRequired, isTermsRequired, providerId });
    }
  };

  const onImageClick = (event: MouseEvent) => {
    event.preventDefault();
  };

  const cardImageClass = buildClassName(styles.image, !showsListing && styles['blur-image'], useExpStyle && styles.exp);
  const listingCardClass = buildClassName(styles.component, useExpStyle && styles.exp);

  useEffect(() => {
    if (listingCardElement.current) {
      setIsSmallCard(listingCardElement.current.offsetWidth < 280);
    }
  }, [windowWidth]);


  const trackNoModalEvents = () => {
    if (gtmOnClickEvent) {
      trackEvent(gtmOnClickEvent);
    }
    trackEvent('Xp_UiAddresspView');
  };

  return (
    <ConditionalWrapper condition={noModal} wrapper={children => <Link href={addressPathWithFallback} target={noModal ? '_blank' : ''} rel={!listing.displayAddress ? 'nofollow' : undefined} onClick={trackNoModalEvents}>{children}</Link>}>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <div className={listingCardClass} ref={listingCardElement} onClick={openListingModal} data-testid={listingCardIds.listingCard} dynamic-testid={String(listingId)}>
        <div className={buildClassName(styles.row, useExpStyle && styles['exp'])}>
          <AspectRatio ratio="4:3">
            <ConditionalWrapper condition={!noModal && isImageReady} wrapper={children => <Link href={addressPathWithFallback} onClick={onImageClick}>{children}</Link>}>
              <ListingImage
                item={{ image: getImageSrc(imageUrl), alt: `${imageAlt} | Card Image`, id: `${listing.id}-card-image`, isImageReady, addedAt: listing.addedAt }}
                isVisible={isImageVisible}
                className={cardImageClass}
                testId={listingCardIds.listingImage}
                usePriorityMode={isCrawler}
                isListingCardImage={true}
              />
            </ConditionalWrapper>
            {isHidden &&
              <ListingCardNewImageOverlay />
            }
            {pvrSentDate && <p className={styles['pvr-date']}>Request sent on {formatDateWithTemplate(pvrSentDate, 'MMM DD, YYYY')}</p>}
          </AspectRatio>
        </div>
        <div className={styles.wrapper}>
          {isHidden || (isTermsRequired && isAuthenticated) || (isTermsRequired && isCrea)
            ? <ListingCardNewOverlay isLoginRequired={isHidden} isTermsRequired={isTermsRequired} isSmallCard={isSmallCard} isCrea={isCrea} />
            : <div className={styles['bottom-content']}>
              <div>
                {showsListing && <ListingStatus status={listing.standardStatus} isRental={listing.isRental} className={styles.tag} />}
                {showsListing && !isWidget && <FavouriteToggle listingId={listingId} favouriteEventTracker={favouriteEventTracker} />}
                <div className={styles.row} data-testid={listingCardIds.listingCardPrice}>
                  <ListingPrice listing={listing} isVisible={showsListing} isMinimal={true} testId={listingCardIds.listingPrice} shouldScramble={shouldScramble} />
                  <ListingTimestamp listing={listing} isSmallCard={isSmallCard} isMinimal={true} className={styles['date-added']} testId={listingCardIds.listingTimeStamp} isVisible={showsListing} shouldScramble={shouldScramble} />
                </div>
                <div className={styles.row}>
                  <SchemaOrg itemType="SingleFamilyResidence">
                    <SchemaOrg itemProp="address" itemType="PostalAddress">
                      {noModal ?
                        <div className={styles['street-address']} data-testid={listingCardIds.listingAddress}>
                          <p>{displayAddress ? generateFullAddress(street, city, province, shouldScramble) : 'Address not available'}</p>
                        </div> :
                        <p className={styles['street-address']} data-testid={listingCardIds.listingAddress}>
                          <Link href={addressPathWithFallback} target="_blank">
                            {displayAddress ? generateFullAddress(street, city, province, shouldScramble) : 'Address not available'}
                          </Link>
                        </p>
                      }
                      <meta itemProp="streetAddress" content={displayAddress && street ? street : ''} />
                      <meta itemProp="addressLocality" content={city || ''} />
                      <meta itemProp="addressRegion" content={province || ''} />
                    </SchemaOrg>
                    <SchemaOrg itemProp="geo" itemType="GeoCoordinates">
                      <meta itemProp="latitude" content={position ? position.latitude.toString() : ''} />
                      <meta itemProp="longitude" content={position ? position.longitude.toString() : ''} />
                    </SchemaOrg>
                  </SchemaOrg>
                </div>
                <ListingBedBathSqftLabels listing={listing} listingToCompare={listingToCompare} obfuscate={isHidden || isTermsRequired} tenantId={tenantId} />
              </div>
              <div>
                <ProviderLabels
                  isBlurred={isHidden || isTermsRequired}
                  hasLogo={isUsStateCode(province?.toLowerCase() as ProvinceOrStateCode)}
                  brokerage={listing.brokerage}
                  mlsNum={listing.mlsNum}
                />
                <ProviderLogo listing={listing} isOnMapSidePanel={isOnMapSidePanel} />
              </div>
            </div>
          }
        </div>
      </div>
    </ConditionalWrapper>
  );
}